import "./styles.css";
import { evaluateCell } from "../src/shared/formula";

type User = { id: string; display_name: string; email: string | null; auth_method: "google" | "name" };
type Hint = { level: "full" | "brief" | "none"; text: string };
type Choice = { id: string; text: string };
type Lesson = {
  slug: string;
  title: string;
  why: string;
  task: string;
  grid: Record<string, string | number>;
  editable: string[];
  hintSteps: Hint[];
  savedMinutesPerUse: number;
  mobileChoices: Choice[];
};
type Progress = {
  lesson_slug: string;
  hint_level: number;
  status: "in_progress" | "completed";
  points: number;
};
type Leader = { rank: number; user_id: string; display_name: string; points: number; completed_count: number };
type Bootstrap = {
  user: User | null;
  googleEnabled: boolean;
  lessons: Lesson[];
  progress: Progress[];
  leaderboard: Leader[];
  stats: { points: number; completed: number; savedMinutes: number; rank: number | null };
};

const root = document.querySelector<HTMLDivElement>("#app")!;
let data: Bootstrap;
let mode: "desktop" | "mobile" = matchMedia("(max-width: 700px)").matches ? "mobile" : "desktop";

const esc = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(body.error || "通信に失敗しました");
  return body;
}

function brand() {
  return `<span class="brand-mark" aria-hidden="true"><i></i><i></i><i></i><i></i></span>Work Allegro`;
}

function shell(content: string, user = data?.user) {
  root.innerHTML = `<div class="shell">
    <header class="topbar">
      <a class="brand" href="/" data-route>${brand()}</a>
      <nav class="nav-actions" aria-label="メインナビゲーション">
        ${user ? `<a class="nav-link" href="/" data-route><span>ダッシュボード</span></a>
          <a class="nav-link" href="/learn" data-route><span>コース</span></a>
          <span class="avatar" title="${esc(user.display_name)}">${esc(user.display_name.slice(0, 1))}</span>
          <form method="post" action="/auth/logout"><button class="icon-button" aria-label="ログアウト">退出</button></form>` : ""}
      </nav>
    </header>
    ${content}
  </div>`;
  bindRoutes();
}

function bindRoutes() {
  document.querySelectorAll<HTMLAnchorElement>("a[data-route]").forEach((link) => {
    link.addEventListener("click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      history.pushState({}, "", link.href);
      renderRoute();
    });
  });
}

function authScreen() {
  const error = new URLSearchParams(location.search).get("error");
  shell(`<main id="app-main" class="page">
    <div class="auth-grid">
      <section class="panel hero-panel">
        <p class="eyebrow">5-minute practice</p>
        <h1>見たテクを、<br>使える操作へ。</h1>
        <p class="lede">あなたの業務で使うExcel操作だけを、本物に近い表で反復。ヒントがなくても手が動くところまで練習します。</p>
        <div class="button-row"><span class="badge">PCは実操作</span><span class="badge">スマホは選択式</span><span class="badge">10コース</span></div>
      </section>
      <section class="panel auth-card" aria-labelledby="auth-title">
        <p class="eyebrow">Start</p><h2 id="auth-title">無料で学習を始める</h2>
        <p class="muted">進捗とポイントを残すため、最初にアカウントを選びます。</p>
        ${error ? `<p class="error-banner" role="alert">${esc(error)}</p>` : ""}
        ${data.googleEnabled ? `<a class="button google" href="/auth/google">Googleで続ける</a><div class="divider">または</div>` : ""}
        <form method="post" action="/auth/identify">
          <label for="display_name">お名前</label>
          <input id="display_name" name="display_name" autocomplete="name" maxlength="80" required placeholder="山田 太郎">
          <label for="email">メールアドレス</label>
          <input id="email" name="email" type="email" autocomplete="email" maxlength="200" required placeholder="you@example.com">
          <button class="button ${data.googleEnabled ? "secondary" : ""}" type="submit">名前とメールで始める</button>
        </form>
        <p class="fine">学習記録とアーリーアクセス運営のために使用し、ランキングにメールアドレスは表示しません。</p>
      </section>
    </div>
  </main>`, null);
}

function progressFor(slug: string) {
  return data.progress.find((item) => item.lesson_slug === slug);
}

function lessonCard(lesson: Lesson, index: number) {
  const progress = progressFor(lesson.slug);
  const complete = progress?.status === "completed";
  const percent = complete ? 100 : progress ? Math.min(66, (progress.hint_level + 1) * 33) : 0;
  return `<article class="panel lesson-card">
    <div class="lesson-meta"><span>Course ${String(index + 1).padStart(2, "0")}</span><span>約5分</span>
      <span class="badge ${complete ? "complete" : ""}">${complete ? "定着済み" : progress ? "練習中" : "未着手"}</span></div>
    <h3>${esc(lesson.title)}</h3><p class="muted">${esc(lesson.why)}</p>
    <div class="progress-track" aria-label="進捗 ${percent}%"><span style="width:${percent}%"></span></div>
    <a class="button small ${complete ? "secondary" : ""}" href="/learn/${encodeURIComponent(lesson.slug)}" data-route>${complete ? "もう一度練習" : progress ? "続きから" : "始める"}</a>
  </article>`;
}

function dashboard() {
  const user = data.user!;
  const next = data.lessons.find((lesson) => progressFor(lesson.slug)?.status !== "completed") ?? data.lessons[0];
  const saving = data.stats.savedMinutes >= 60
    ? `${Math.floor(data.stats.savedMinutes / 60)}時間${data.stats.savedMinutes % 60 ? `${data.stats.savedMinutes % 60}分` : ""}`
    : `${data.stats.savedMinutes}分`;
  shell(`<main id="app-main" class="page">
    <section class="panel hero-panel">
      <p class="eyebrow">Dashboard</p><h1>${esc(user.display_name)}さん、<br>今日も1つ速くなる。</h1>
      <p class="lede">次は「${esc(next.title)}」。短い反復を終えるたび、現場で迷わない操作が増えていきます。</p>
      <div class="button-row"><a class="button" href="/learn/${encodeURIComponent(next.slug)}" data-route>次の5分を始める</a><a class="button secondary" href="/learn" data-route>全コースを見る</a></div>
    </section>
    <section class="stats" aria-label="学習サマリー">
      <article class="panel stat"><span class="stat-value">${data.stats.points}</span><span class="stat-label">累計ポイント</span></article>
      <article class="panel stat"><span class="stat-value">${data.stats.completed}<small> / ${data.lessons.length}</small></span><span class="stat-label">定着したコース</span></article>
      <article class="panel stat"><span class="stat-value">${saving}</span><span class="stat-label">想定短縮時間の合計</span></article>
      <article class="panel stat"><span class="stat-value">${data.stats.rank ? `${data.stats.rank}位` : "未参加"}</span><span class="stat-label">実ユーザーランキング</span></article>
    </section>
    <p class="fine">想定短縮時間は教材ごとの目安を合算した参考値で、職場での実測削減時間ではありません。</p>
    <div class="section-head"><div><p class="eyebrow">Your courses</p><h2>業務で使う型を、順番に</h2></div><a href="/learn" data-route>10コースをすべて見る</a></div>
    <div class="dashboard-grid">
      <section class="lesson-grid">${data.lessons.slice(0, 4).map(lessonCard).join("")}</section>
      ${leaderboardPanel()}
    </div>
  </main>`, user);
}

function leaderboardPanel() {
  return `<aside class="panel leaderboard"><p class="eyebrow">Ranking</p><h2>今週の練習者</h2>
    ${data.leaderboard.length ? data.leaderboard.slice(0, 8).map((row) => `<div class="rank-row ${row.user_id === data.user?.id ? "current" : ""}">
      <span class="rank-num">${row.rank}</span><span>${esc(row.display_name)}<br><small class="muted">${row.completed_count}コース</small></span><span class="rank-points">${row.points} pt</span>
    </div>`).join("") : `<p class="muted">最初のノーヒント課題を終えると参加できます。</p>`}
    <p class="fine">表示名・ポイント・完了数のみ公開しています。</p>
  </aside>`;
}

function courseList() {
  shell(`<main id="app-main" class="page">
    <p class="eyebrow">Course library</p><h1>現場の10テクニック</h1>
    <p class="lede">関数を網羅するのではなく、転記・照合・集計で繰り返し使う操作だけ。各コースはフルヒント、要点のみ、ノーヒントの3回で定着させます。</p>
    <div class="section-head"><h2>すべてのコース</h2><span class="muted">${data.stats.completed} / ${data.lessons.length} 完了</span></div>
    <section class="lesson-grid">${data.lessons.map(lessonCard).join("")}</section>
  </main>`);
}

function coord(address: string): [number, number] {
  const match = address.replaceAll("$", "").match(/^([A-Z]+)(\d+)$/)!;
  let col = 0;
  for (const char of match[1]) col = col * 26 + char.charCodeAt(0) - 64;
  return [Number(match[2]), col];
}

function address(row: number, col: number) {
  let letters = "";
  for (let n = col; n > 0; n = Math.floor((n - 1) / 26)) letters = String.fromCharCode(((n - 1) % 26) + 65) + letters;
  return `${letters}${row}`;
}

function safeGrid(lesson: Lesson) {
  return Object.fromEntries(Object.entries(lesson.grid).map(([key, value]) => [key.replaceAll("$", "").toUpperCase(), String(value)]));
}

function desktopPlayer(lesson: Lesson, level: number) {
  const values = safeGrid(lesson);
  lesson.editable.forEach((cell) => { values[cell] ??= ""; });
  const keys = Object.keys(values).map(coord);
  const rows = Math.max(8, ...keys.map(([row]) => row));
  const cols = Math.max(6, ...keys.map(([, col]) => col));
  let selected = lesson.editable[0];
  let editing = false;
  let editOriginal = "";
  let dragStart: string | null = null;
  let dragEnd: string | null = null;
  const displayValue = (key: string) => {
    if (editing && selected === key) return values[key] ?? "";
    try {
      return String(evaluateCell(key, values));
    } catch {
      return values[key]?.startsWith("=") ? "#VALUE!" : values[key] ?? "";
    }
  };

  const renderGrid = () => {
    const grid = document.querySelector<HTMLDivElement>("#excel-grid")!;
    grid.style.gridTemplateColumns = `42px repeat(${cols}, minmax(112px, 1fr))`;
    let html = `<div class="cell corner"></div>`;
    for (let col = 1; col <= cols; col++) html += `<div class="cell col-head">${address(1, col).replace(/\d+/, "")}</div>`;
    for (let row = 1; row <= rows; row++) {
      html += `<div class="cell row-head">${row}</div>`;
      for (let col = 1; col <= cols; col++) {
        const key = address(row, col);
        const inRange = dragStart && dragEnd ? isWithin(key, dragStart, dragEnd) : false;
        html += `<div class="cell ${selected === key ? "selected" : ""} ${lesson.editable.includes(key) ? "editable" : ""} ${editing && selected === key ? "editing" : ""} ${inRange ? "range" : ""}" data-cell="${key}" role="gridcell" aria-selected="${selected === key}">${esc(displayValue(key))}</div>`;
      }
    }
    grid.innerHTML = html;
    document.querySelector<HTMLElement>("#name-box")!.textContent = selected;
    const input = document.querySelector<HTMLInputElement>("#formula-input")!;
    if (document.activeElement !== input) input.value = values[selected] ?? "";
    bindCells();
  };

  const insertReference = (ref: string) => {
    const input = document.querySelector<HTMLInputElement>("#formula-input")!;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    input.value = input.value.slice(0, start) + ref + input.value.slice(end);
    values[selected] = input.value;
    input.focus();
    input.setSelectionRange(start + ref.length, start + ref.length);
  };

  const paintRange = () => {
    document.querySelectorAll<HTMLElement>("[data-cell]").forEach((item) => {
      item.classList.toggle("range", Boolean(dragStart && dragEnd && isWithin(item.dataset.cell!, dragStart, dragEnd)));
    });
  };

  const finishDrag = () => {
    if (!dragStart || !dragEnd) return;
    const ref = dragStart === dragEnd ? dragStart : `${dragStart}:${dragEnd}`;
    dragStart = null; dragEnd = null;
    insertReference(ref);
    renderGrid();
  };

  const bindCells = () => {
    document.querySelectorAll<HTMLElement>("[data-cell]").forEach((cell) => {
      cell.addEventListener("pointerdown", (event) => {
        const key = cell.dataset.cell!;
        const formula = values[selected]?.startsWith("=");
        if (editing && formula && key !== selected) {
          event.preventDefault();
          dragStart = key; dragEnd = key;
          paintRange();
          return;
        }
        selected = key;
        editing = false;
        renderGrid();
      });
      cell.addEventListener("pointerenter", () => {
        if (!dragStart) return;
        dragEnd = cell.dataset.cell!;
        paintRange();
      });
      cell.addEventListener("pointerup", finishDrag);
      cell.addEventListener("dblclick", () => beginEdit(cell.dataset.cell!));
    });
  };

  const beginEdit = (key = selected) => {
    if (!lesson.editable.includes(key)) return;
    selected = key;
    if (!editing) editOriginal = values[key] ?? "";
    editing = true; renderGrid();
    const input = document.querySelector<HTMLInputElement>("#formula-input")!;
    input.focus(); input.setSelectionRange(input.value.length, input.value.length);
  };

  const move = (rowDelta: number, colDelta: number) => {
    const [row, col] = coord(selected);
    selected = address(Math.max(1, Math.min(rows, row + rowDelta)), Math.max(1, Math.min(cols, col + colDelta)));
    editing = false; renderGrid();
    document.querySelector<HTMLElement>(`[data-cell="${selected}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  document.querySelector<HTMLDivElement>("#practice-host")!.innerHTML = `<div class="workbook" id="workbook" role="grid" tabindex="0" aria-label="Excel練習シート">
    <div class="formula-row"><span class="name-box" id="name-box"></span><span class="fx">fx</span><input id="formula-input" class="formula-input" aria-label="数式バー" autocomplete="off" spellcheck="false"></div>
    <div class="grid-scroll"><div class="excel-grid" id="excel-grid"></div></div>
    <div class="grid-tip">矢印で移動 · Enterで下へ · Tabで右へ · F2で編集 · 数式中はセルをクリック／ドラッグして参照</div>
  </div>`;
  const formulaInput = document.querySelector<HTMLInputElement>("#formula-input")!;
  formulaInput.addEventListener("focus", () => {
    if (lesson.editable.includes(selected)) {
      if (!editing) editOriginal = values[selected] ?? "";
      editing = true;
    }
    renderGrid();
  });
  formulaInput.addEventListener("input", () => { if (lesson.editable.includes(selected)) values[selected] = formulaInput.value; });
  formulaInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault(); values[selected] = formulaInput.value; move(event.key === "Enter" ? 1 : 0, event.key === "Tab" ? 1 : 0);
    } else if (event.key === "Escape") {
      event.preventDefault();
      values[selected] = editOriginal;
      editing = false;
      formulaInput.blur();
      renderGrid();
    }
  });
  document.querySelector("#workbook")!.addEventListener("keydown", (event) => {
    if (document.activeElement === formulaInput) return;
    const key = (event as KeyboardEvent).key;
    if (key === "F2") { event.preventDefault(); beginEdit(); }
    else if (key === "ArrowUp") { event.preventDefault(); move(-1, 0); }
    else if (key === "ArrowDown" || key === "Enter") { event.preventDefault(); move(1, 0); }
    else if (key === "ArrowLeft") { event.preventDefault(); move(0, -1); }
    else if (key === "ArrowRight" || key === "Tab") { event.preventDefault(); move(0, 1); }
    else if (key.length === 1 && lesson.editable.includes(selected)) {
      event.preventDefault(); values[selected] = key; beginEdit();
      requestAnimationFrame(() => { formulaInput.value = key; formulaInput.setSelectionRange(1, 1); });
    }
  });
  document.querySelector<HTMLButtonElement>("#submit-answer")!.onclick = () => submitLesson(lesson, level, { answers: Object.fromEntries(lesson.editable.map((key) => [key, values[key] ?? ""])) });
  renderGrid();
}

function isWithin(key: string, start: string, end: string) {
  const [r, c] = coord(key), [r1, c1] = coord(start), [r2, c2] = coord(end);
  return r >= Math.min(r1, r2) && r <= Math.max(r1, r2) && c >= Math.min(c1, c2) && c <= Math.max(c1, c2);
}

function mobilePlayer(lesson: Lesson, level: number) {
  const entries = Object.entries(lesson.grid).slice(0, 16);
  let choice = "";
  document.querySelector<HTMLDivElement>("#practice-host")!.innerHTML = `<div class="panel mobile-practice">
    <div class="mobile-sheet"><table><thead><tr><th>セル</th><th>内容</th></tr></thead><tbody>
      ${entries.map(([cell, value]) => `<tr><th>${esc(cell)}</th><td class="${lesson.editable.includes(cell) ? "target" : ""}">${esc(value)}</td></tr>`).join("")}
    </tbody></table></div>
    <div class="choices" role="radiogroup" aria-label="操作を選ぶ">${lesson.mobileChoices.map((item) => `<button class="choice" type="button" role="radio" aria-checked="false" data-choice="${esc(item.id)}">${esc(item.text)}</button>`).join("")}</div>
  </div>`;
  document.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((button) => {
    button.onclick = () => {
      choice = button.dataset.choice!;
      document.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((item) => {
        const selected = item === button; item.classList.toggle("selected", selected); item.setAttribute("aria-checked", String(selected));
      });
    };
  });
  document.querySelector<HTMLButtonElement>("#submit-answer")!.onclick = () => {
    if (!choice) return showFeedback("操作を1つ選んでください。", "error");
    submitLesson(lesson, level, { choiceId: choice });
  };
}

function showFeedback(message: string, kind: "ok" | "nudge" | "error") {
  const target = document.querySelector<HTMLDivElement>("#feedback")!;
  target.className = `feedback ${kind}`; target.textContent = message; target.hidden = false;
}

async function submitLesson(lesson: Lesson, level: number, payload: object) {
  const button = document.querySelector<HTMLButtonElement>("#submit-answer")!;
  button.disabled = true;
  try {
    const result = await api<{ result: "correct" | "nudge" | "incorrect"; message: string; completed: boolean; nextLevel: number; bootstrap?: Bootstrap }>(
      `/api/lessons/${encodeURIComponent(lesson.slug)}/submit`,
      { method: "POST", body: JSON.stringify({ level, mode, ...payload }) },
    );
    if (result.result !== "correct") {
      showFeedback(result.message, result.result === "nudge" ? "nudge" : "error");
      return;
    }
    showFeedback(result.message, "ok");
    if (result.bootstrap) data = result.bootstrap;
    setTimeout(() => {
      if (result.completed) {
        history.pushState({}, "", "/");
        renderRoute();
        toast("コース完了。100ポイントを獲得しました。");
      } else {
        renderPlayer(lesson.slug, result.nextLevel);
      }
    }, 650);
  } catch (error) {
    showFeedback(error instanceof Error ? error.message : "送信できませんでした。", "error");
  } finally {
    button.disabled = false;
  }
}

function toast(message: string) {
  const el = document.createElement("div"); el.className = "toast"; el.textContent = message; document.body.appendChild(el);
  setTimeout(() => el.remove(), 3200);
}

function renderPlayer(slug: string, forcedLevel?: number) {
  const lesson = data.lessons.find((item) => item.slug === slug);
  if (!lesson) return notFound();
  const progress = progressFor(slug);
  const level = forcedLevel ?? (progress?.status === "completed" ? 0 : Math.min(2, (progress?.hint_level ?? -1) + 1));
  const hint = lesson.hintSteps[level];
  shell(`<main id="app-main" class="page player-page">
    <div class="player-toolbar">
      <a href="/learn" data-route class="nav-link">← コース</a>
      <div class="mode-switch" aria-label="練習モード">
        <button type="button" data-mode="desktop" class="${mode === "desktop" ? "active" : ""}">PC実践</button>
        <button type="button" data-mode="mobile" class="${mode === "mobile" ? "active" : ""}">スマホ練習</button>
      </div>
    </div>
    <div class="player-layout">
      <aside class="panel instruction">
        <p class="eyebrow">Step ${level + 1} / 3</p><h2>${esc(lesson.title)}</h2><p class="muted">${esc(lesson.task)}</p>
        <div class="step-dots" aria-label="${level + 1}段階目">${[0,1,2].map((n) => `<i class="${n < level ? "done" : n === level ? "active" : ""}"></i>`).join("")}</div>
        <div class="hint-box"><strong>${level === 0 ? "やってみよう" : level === 1 ? "要点だけ" : "ノーヒント"}</strong><br>${hint.text ? esc(hint.text) : "実務の場面を思い出して、自力で完成させましょう。"}</div>
        <button class="button" type="button" id="submit-answer">答えを確認</button>
        <div id="feedback" hidden></div>
        <p class="fine">ノーヒントで正解すると100ポイント。想定短縮 ${lesson.savedMinutesPerUse}分は参考値です。</p>
      </aside>
      <section id="practice-host" aria-label="練習エリア"></section>
    </div>
  </main>`);
  document.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((button) => {
    button.onclick = () => { mode = button.dataset.mode as typeof mode; renderPlayer(slug, level); };
  });
  if (mode === "desktop") desktopPlayer(lesson, level);
  else mobilePlayer(lesson, level);
}

function notFound() {
  shell(`<main class="page narrow"><p class="eyebrow">404</p><h1>ページが見つかりません</h1><a class="button" href="/" data-route>ホームへ戻る</a></main>`);
}

function renderRoute() {
  const path = location.pathname;
  if (!data.user) return authScreen();
  if (path === "/") return dashboard();
  if (path === "/learn") return courseList();
  const match = path.match(/^\/learn\/([^/]+)$/);
  if (match) return renderPlayer(decodeURIComponent(match[1]));
  notFound();
}

addEventListener("popstate", renderRoute);

async function start() {
  try {
    data = await api<Bootstrap>("/api/bootstrap");
    renderRoute();
  } catch (error) {
    root.innerHTML = `<main class="page narrow"><p class="eyebrow">Connection error</p><h1>読み込めませんでした</h1><p class="lede">${esc(error instanceof Error ? error.message : "時間をおいて再度お試しください。")}</p><button class="button" onclick="location.reload()">再読み込み</button></main>`;
  }
}

void start();
